// frontend-dash/src/pages/PendingRegistrations.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { getRoleLabel } from '../utils/permissions';
import {
    AddUserIcon as UserPlus,
    CheckCircleIcon as CheckCircle,
    CloseIcon as X,
    MailIcon as Mail,
    CalendarIcon as Calendar,
    AlertCircleIcon as AlertCircle,
    TeamIcon as Users,
} from '../components/Icons';

const PendingRegistrations = () => {
    const { profile, loadingProfile } = useOutletContext();
    const { user, API_URL } = useContext(AuthContext);

    const [pendingUsers, setPendingUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [message, setMessage] = useState(null);
    const [rejectConfirm, setRejectConfirm] = useState(null);

    const config = useCallback(() => ({
        headers: { Authorization: `Bearer ${user?.token}` }
    }), [user]);

    const fetchPending = useCallback(async () => {
        if (!user?.token) return;
        try {
            const { data } = await axios.get(`${API_URL}/users/unapproved`, config());
            setPendingUsers(data);
        } catch (err) {
            console.error('Fehler beim Laden:', err);
        } finally {
            setLoading(false);
        }
    }, [user, API_URL, config]);

    useEffect(() => {
        fetchPending();
    }, [fetchPending]);

    const showMessage = (text, isError = false) => {
        setMessage({ text, isError });
        setTimeout(() => setMessage(null), 5000);
    };

    const handleApprove = async (userId, userName) => {
        setActionLoading(userId);
        try {
            await axios.put(`${API_URL}/users/${userId}/approve`, { isApproved: true }, config());
            showMessage(`${userName} wurde freigeschaltet! Eine Bestätigungs-E-Mail wurde gesendet.`);
            setPendingUsers(prev => prev.filter(u => u._id !== userId));
        } catch (err) {
            showMessage(err.response?.data?.message || 'Fehler beim Freischalten.', true);
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (userId, userName) => {
        setActionLoading(userId);
        try {
            await axios.delete(`${API_URL}/users/${userId}`, config());
            showMessage(`Registrierung von ${userName} wurde abgelehnt und gelöscht.`);
            setPendingUsers(prev => prev.filter(u => u._id !== userId));
            setRejectConfirm(null);
        } catch (err) {
            showMessage(err.response?.data?.message || 'Fehler beim Ablehnen.', true);
        } finally {
            setActionLoading(null);
        }
    };

    if (loadingProfile) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!profile || (profile.role !== 'admin' && profile.role !== 'gruppenleiter')) {
        return (
            <div className="md3-card-elevated p-8 bg-error-container text-error-on-container m-6">
                <h2 className="text-headline-small font-bold mb-2">Zugriff verweigert</h2>
                <p>Nur Admins und Gruppenleiter können Registrierungen verwalten.</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 max-w-5xl animate-fade-in">
            {/* Header */}
            <div className="mb-10">
                <div className="flex items-center gap-3 text-primary mb-2">
                    <UserPlus style={{ fontSize: 28 }} />
                    <h1 className="text-display-small font-bold text-surface-on">Registrierungsanfragen</h1>
                </div>
                <p className="text-body-large text-surface-onVariant">
                    {pendingUsers.length === 0
                        ? 'Keine offenen Anfragen.'
                        : `${pendingUsers.length} ${pendingUsers.length === 1 ? 'Anfrage' : 'Anfragen'} warten auf Freischaltung.`
                    }
                </p>
            </div>

            {/* Message */}
            {message && (
                <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 shadow-sm animate-fade-in ${
                    message.isError
                        ? 'bg-error-container text-error-onContainer'
                        : 'bg-primary-container text-primary-onContainer'
                }`}>
                    {message.isError ? <AlertCircle style={{ fontSize: 20 }} /> : <CheckCircle style={{ fontSize: 20 }} />}
                    <span className="font-medium">{message.text}</span>
                </div>
            )}

            {/* Loading */}
            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                </div>
            ) : pendingUsers.length === 0 ? (
                /* Empty State */
                <div className="md3-card-elevated p-16 text-center">
                    <CheckCircle style={{ fontSize: 56 }} className="text-primary/20 mx-auto mb-6" />
                    <h3 className="text-headline-small font-bold text-surface-on mb-2">Alles erledigt!</h3>
                    <p className="text-body-large text-surface-onVariant">
                        Es gibt keine ausstehenden Registrierungsanfragen.
                    </p>
                </div>
            ) : (
                /* Pending Users List */
                <div className="space-y-4">
                    {pendingUsers.map(pendingUser => {
                        const name = `${pendingUser.userData?.firstName || ''} ${pendingUser.userData?.lastName || ''}`.trim() || pendingUser.email;
                        const initials = (pendingUser.userData?.firstName?.[0] || pendingUser.email[0]).toUpperCase();
                        const isActioning = actionLoading === pendingUser._id;

                        return (
                            <div
                                key={pendingUser._id}
                                className="md3-card-elevated p-6 hover:shadow-elevation-2 transition-all animate-fade-in"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    {/* User Info */}
                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                        <div className="w-14 h-14 rounded-2xl bg-tertiary-container text-tertiary-onContainer flex items-center justify-center font-bold text-xl shadow-sm shrink-0">
                                            {initials}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="text-title-large font-bold text-surface-on truncate">
                                                {name}
                                            </h3>
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                                                <span className="flex items-center gap-1.5 text-body-small text-surface-onVariant">
                                                    <Mail style={{ fontSize: 14 }} className="opacity-60" />
                                                    {pendingUser.email}
                                                </span>
                                                <span className="flex items-center gap-1.5 text-body-small text-surface-onVariant">
                                                    <Calendar style={{ fontSize: 14 }} className="opacity-60" />
                                                    {new Date(pendingUser.createdAt).toLocaleDateString('de-DE', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                    })}
                                                </span>
                                                <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-0.5 rounded-full bg-surface-onVariant/10 text-surface-onVariant">
                                                    {getRoleLabel(pendingUser.role)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2 shrink-0">
                                        {rejectConfirm === pendingUser._id ? (
                                            <div className="flex items-center gap-2 animate-fade-in">
                                                <span className="text-label-small text-surface-onVariant mr-1">Wirklich ablehnen?</span>
                                                <button
                                                    onClick={() => handleReject(pendingUser._id, name)}
                                                    disabled={isActioning}
                                                    className="md3-btn-filled !bg-error !text-error-on text-sm px-4 py-2"
                                                >
                                                    {isActioning ? (
                                                        <div className="w-4 h-4 border-2 border-error-on/30 border-t-error-on rounded-full animate-spin"></div>
                                                    ) : 'Ja, ablehnen'}
                                                </button>
                                                <button
                                                    onClick={() => setRejectConfirm(null)}
                                                    className="md3-btn-text text-sm"
                                                >
                                                    Nein
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={() => handleApprove(pendingUser._id, name)}
                                                    disabled={isActioning}
                                                    className="md3-btn-filled gap-2 text-sm px-5 py-2.5 shadow-sm"
                                                >
                                                    {isActioning ? (
                                                        <div className="w-4 h-4 border-2 border-surface/30 border-t-surface rounded-full animate-spin"></div>
                                                    ) : (
                                                        <>
                                                            <CheckCircle style={{ fontSize: 16 }} />
                                                            Freischalten
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => setRejectConfirm(pendingUser._id)}
                                                    disabled={isActioning}
                                                    className="md3-btn-outlined !border-error/30 !text-error hover:!bg-error/5 gap-2 text-sm px-5 py-2.5"
                                                >
                                                    <X style={{ fontSize: 16 }} />
                                                    Ablehnen
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default PendingRegistrations;
